import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Key,
  RefreshCw,
  AlertTriangle,
  Clock,
  Trash2,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import api from "@/services/api";

export const AdminSecurity = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Dialog states
  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  // Rotate form state
  const [rotateForm, setRotateForm] = useState({
    keyType: "both",
    reason: "",
    revokeExisting: false,
    gracePeriodMinutes: "",
  });

  // Revoke confirmation
  const [revokeConfirm, setRevokeConfirm] = useState("");

  // Query: Fetch JWT status
  const {
    data: status,
    isLoading: statusLoading,
    isFetching: statusFetching,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["admin", "security", "jwt", "status"],
    queryFn: async () => {
      const response = await api.get("/admin/security/jwt/status");
      return response.data.data;
    },
  });

  // Query: Fetch JWT stats
  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ["admin", "security", "jwt", "stats"],
    queryFn: async () => {
      const response = await api.get("/admin/security/jwt/stats");
      return response.data.data;
    },
  });

  // Combined loading state
  const loading = statusLoading || statsLoading;
  const isFetching = statusFetching;

  // Mutation: Rotate keys
  const rotateMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await api.post("/admin/security/jwt/rotate", payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.security.keysRotated"));
      setShowRotateDialog(false);
      setRotateForm({
        keyType: "both",
        reason: "",
        revokeExisting: false,
        gracePeriodMinutes: "",
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "security", "jwt"] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || t("admin.security.rotationFailed"),
      );
    },
  });

  // Mutation: Revoke all tokens
  const revokeAllMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post("/admin/security/jwt/revoke-all", {
        confirm: "REVOKE_ALL_TOKENS",
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(
        t("admin.security.tokensRevoked", { count: data.data.revokedCount }),
      );
      setShowRevokeDialog(false);
      setRevokeConfirm("");
      queryClient.invalidateQueries({ queryKey: ["admin", "security", "jwt"] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || t("admin.security.revokeFailed"),
      );
    },
  });

  // Mutation: Cleanup expired tokens
  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post("/admin/security/jwt/cleanup");
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(
        t("admin.security.cleanupSuccess", { count: data.data.deletedCount }),
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "security", "jwt"] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || t("admin.security.cleanupFailed"),
      );
    },
  });

  // Combined action loading state
  const actionLoading =
    rotateMutation.isPending ||
    revokeAllMutation.isPending ||
    cleanupMutation.isPending;

  // Handler: Rotate keys
  const handleRotate = () => {
    if (!rotateForm.reason.trim()) {
      toast.error(t("admin.security.rotationReasonRequired"));
      return;
    }

    const payload = {
      keyType: rotateForm.keyType,
      reason: rotateForm.reason.trim(),
      revokeExisting: rotateForm.revokeExisting,
    };

    if (rotateForm.gracePeriodMinutes) {
      payload.gracePeriodMinutes = parseInt(rotateForm.gracePeriodMinutes, 10);
    }

    rotateMutation.mutate(payload);
  };

  // Handler: Revoke all tokens
  const handleRevokeAll = () => {
    if (revokeConfirm !== "REVOKE_ALL_TOKENS") {
      toast.error(t("admin.security.revokeConfirmRequired"));
      return;
    }
    revokeAllMutation.mutate();
  };

  // Handler: Cleanup expired tokens
  const handleCleanup = () => {
    cleanupMutation.mutate();
  };

  // Handler: Refresh data
  const handleRefresh = () => {
    refetchStatus();
    refetchStats();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-36" />
            <Skeleton className="h-10 w-28" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Show migration warning if table doesn't exist
  if (status && status.tableExists === false) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("settings.security.title")}
            </h1>
            <p className="text-muted-foreground">
              Manage JWT key rotation and token security
            </p>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("admin.security.migrationRequired")}</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{status.message}</p>
            <code className="block bg-muted p-2 rounded text-sm mt-2">
              psql -U escribe -d escribe -f migrations/030_jwt_key_rotation.sql
            </code>
            <p className="mt-2 text-sm">{t("admin.security.migrationHelp")}</p>
          </AlertDescription>
        </Alert>

        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
          />
          {t("common.refresh")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("settings.security.title")}
          </h1>
          <p className="text-muted-foreground">
            Manage JWT key rotation and token security
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCleanup}
            disabled={actionLoading}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Cleanup Expired
          </Button>
          <Button onClick={() => setShowRotateDialog(true)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Rotate Keys
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Access Token Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Key className="h-4 w-4" />
              Access Token Keys
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {status?.access?.activeKeyCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">Active keys</p>
            {status?.access?.primaryKeyCreatedAt && (
              <p className="text-xs text-muted-foreground mt-2">
                Primary created: {formatDate(status.access.primaryKeyCreatedAt)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Refresh Token Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Key className="h-4 w-4" />
              Refresh Token Keys
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {status?.refresh?.activeKeyCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">Active keys</p>
            {status?.refresh?.primaryKeyCreatedAt && (
              <p className="text-xs text-muted-foreground mt-2">
                Primary created:{" "}
                {formatDate(status.refresh.primaryKeyCreatedAt)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Token Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Refresh Token Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Active:</span>
                <Badge variant="default">
                  {stats?.refreshTokens?.active || 0}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Revoked:</span>
                <Badge variant="secondary">
                  {stats?.refreshTokens?.revoked || 0}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Expired:</span>
                <Badge variant="outline">
                  {stats?.refreshTokens?.expired || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Emergency Actions */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Emergency Actions
          </CardTitle>
          <CardDescription>
            Use these actions only in case of a security incident
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => setShowRevokeDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Revoke All Refresh Tokens
          </Button>
          <p className="text-sm text-muted-foreground mt-2">
            This will force all users to log in again immediately.
          </p>
        </CardContent>
      </Card>

      {/* Rotation History */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.security.rotationHistory")}</CardTitle>
          <CardDescription>
            {t("admin.security.rotationHistoryDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status?.history?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("admin.security.keyType")}</TableHead>
                  <TableHead>{t("admin.security.reason")}</TableHead>
                  <TableHead>{t("admin.security.gracePeriod")}</TableHead>
                  <TableHead>{t("admin.security.tokensRevoked")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm">
                      {formatDate(item.rotatedAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.keyType}</Badge>
                    </TableCell>
                    <TableCell className="max-w-50 truncate">
                      {item.reason || "-"}
                    </TableCell>
                    <TableCell>
                      {item.gracePeriodMinutes
                        ? `${item.gracePeriodMinutes} min`
                        : "-"}
                    </TableCell>
                    <TableCell>{item.tokensRevoked || 0}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          item.status === "completed"
                            ? "default"
                            : "destructive"
                        }
                      >
                        {item.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t("admin.security.noRotationHistory")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rotate Keys Dialog */}
      <Dialog open={showRotateDialog} onOpenChange={setShowRotateDialog}>
        <DialogContent className="sm:max-w-125">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {t("admin.security.rotateJwtKeys")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.security.rotateDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("admin.security.keyType")}</Label>
              <Select
                value={rotateForm.keyType}
                onValueChange={(v) =>
                  setRotateForm({ ...rotateForm, keyType: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="access">
                    {t("admin.security.keyTypes.access")}
                  </SelectItem>
                  <SelectItem value="refresh">
                    {t("admin.security.keyTypes.refresh")}
                  </SelectItem>
                  <SelectItem value="both">
                    {t("admin.security.keyTypes.both")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("admin.security.reason")} *</Label>
              <Textarea
                placeholder={t("admin.security.reasonPlaceholder")}
                value={rotateForm.reason}
                onChange={(e) =>
                  setRotateForm({ ...rotateForm, reason: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t("admin.security.gracePeriod")} ({t("common.minutes")})
              </Label>
              <Input
                type="number"
                placeholder={
                  rotateForm.keyType === "access"
                    ? "20 (default for access)"
                    : "10080 (default for refresh, 7 days)"
                }
                value={rotateForm.gracePeriodMinutes}
                onChange={(e) =>
                  setRotateForm({
                    ...rotateForm,
                    gracePeriodMinutes: e.target.value,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("admin.security.gracePeriodHelp")}
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>{t("admin.security.revokeExisting")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("admin.security.revokeExistingDesc")}
                </p>
              </div>
              <Switch
                checked={rotateForm.revokeExisting}
                onCheckedChange={(v) =>
                  setRotateForm({ ...rotateForm, revokeExisting: v })
                }
              />
            </div>

            {rotateForm.revokeExisting && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("common.warning")}</AlertTitle>
                <AlertDescription>
                  {t("admin.security.revokeWarning")}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRotateDialog(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleRotate} disabled={actionLoading}>
              {actionLoading && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Rotate Keys
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke All Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Revoke All Refresh Tokens
            </DialogTitle>
            <DialogDescription>
              This will immediately invalidate all active sessions. All users
              will need to log in again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("security.dangerZone")}</AlertTitle>
              <AlertDescription>
                This action cannot be undone.{" "}
                {stats?.refreshTokens?.active || 0} active tokens will be
                revoked.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Type REVOKE_ALL_TOKENS to confirm</Label>
              <Input
                value={revokeConfirm}
                onChange={(e) => setRevokeConfirm(e.target.value)}
                placeholder={t("placeholders.revokeAllTokens")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRevokeDialog(false);
                setRevokeConfirm("");
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevokeAll}
              disabled={actionLoading || revokeConfirm !== "REVOKE_ALL_TOKENS"}
            >
              {actionLoading && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Revoke All Tokens
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSecurity;
